import { initialize as initializeFeed } from '@finn-no/feed';
import { writeExperimentData } from '@finn-no/unleash-cookie/unleash-cookie.js';

import { debouncedLoadTags } from './podlet/ast';
import {
    Filter,
    HorseshoeConfig,
    KeywordsConfig,
} from '../shared/types/podlet';
import { getPodletConfigFromHtml } from './podlet/config';
import {
    verticalSpecificContentMarketing,
    verticalSpesificInit,
    verticalSpesificUpdate,
} from './podlet/vertical';
import { subscribe, publish } from './podlet/events';
import {
    Keywords,
    Apntag,
    PageType,
    Vertical,
    Subvertical,
    BannerGlobal,
    Finn,
} from '../shared/types/ast';
import { requestBanners } from './utils/banners';
import { renderHorseshoe } from './components/horseshoe/horseshoe-render';
import { renderSkyscraper } from './components/skyscraper/skyscraper-render';
import { isEmpty } from '../shared/utils';

import { version } from '../shared/version';
import { filterKeywordsLegacy } from '../shared/filter';
import {
    filterKeywords,
    filterFeed,
    filterFeedString,
} from '../shared/url-lookup/url-lookup';
import { getGeoFilterLocationKeywords } from '../shared/url-lookup/geo-location-filter-handler';
import { verticalSpesificPreProccessing } from '../shared/appnexus/filter-pre-processing';
import { getSearchReklameConfig } from '../shared/reklame-master-provider/reklame-master-provider';

import {
    classifiedKeywords,
    MappedKeyWords,
} from '../shared/search-query-classifier/search-query-classifier';
import diagnostics from './utils/diagnostics';
import {
    markBannerRefresh,
    markHorseshoeInitiated,
    markHorseshoeLoaded,
    markFilterUpdated,
    markPageLoaded,
    markSetPageOpts,
} from './utils/performance';

import { getFeatureStatus } from './config';
import { Feed, ReklameConfiguration } from '../shared/types/reklameMaster';
import { renderBoard } from './components/board/board-render';
import { renderContentBoard } from './components/content-board/content-board-render';
import { getGlimrKeywords, loadGlimrIfPermission } from './utils/glimr';
import { UNLEASH_FEATURE_NAMES } from '../shared/unleash-feature-names';
import { copyQueue } from './stories/helper';

window.FINN = window.FINN || ({} as Finn);
window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
window.FINN.banner.queue = window.FINN.banner.queue || [];
window.FINN.banner.debouncedLoadTags =
    window.FINN.banner.debouncedLoadTags || debouncedLoadTags();

window.apntag = window.apntag || ({} as Apntag);
window.apntag.anq = window.apntag.anq || [];

let currentFilter: Filter | undefined;
let currentKeywordsConfig: Partial<KeywordsConfig> = {};
let currentFilterKeywords = {};
let currentPageType: PageType | undefined;
let currentSubvertical: Subvertical | undefined;
let keywords: Keywords;
let baseKeywords: Keywords | undefined;
let clientReady = false;
let cycle = 0;

export function getFilterData(config: HorseshoeConfig) {
    const filterData = config.filter;
    if (filterData) {
        if (filterData.page) {
            filterData.page = Number(filterData.page);
        }
        if (filterData.side) {
            filterData.side = Number(filterData.side);
        }
    }
    return filterData;
}

/*
Legacy handling of command based event
 */
function updateKeywords(filter: Filter) {
    const vertical = keywords['no-sno-finn-section'];
    const update = filterKeywordsLegacy(vertical, filter, currentFilter);
    currentFilter = filter;

    if (update.keywords) {
        keywords = { ...baseKeywords, ...update.keywords };
        if (window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.setPageOpts({
                        keywords,
                    });
                }
            });
        }
    }

    if (update.refresh) {
        if (window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.refresh();
                }
            });
        }
    }
}

function diff(existing: {}, update: any) {
    return JSON.stringify(existing) !== JSON.stringify(update);
}

function pageChange(existingFilter?: Filter, updateFilter?: Filter) {
    if (updateFilter?.pageType) {
        return true;
    }
    return (
        existingFilter?.page !== updateFilter?.page ||
        existingFilter?.side !== updateFilter?.side
    );
}

function shouldRefresh(vertical: string, keywordDiff = false) {
    switch (vertical) {
        case 'car':
        case 'bap':
            return keywordDiff;
        default:
            return true;
    }
}

function updateFeed(filter: Filter, nrmFeed: Feed) {
    const enableNRM = getFeatureStatus('enableNRM');
    const bannerUrlFeed = filterFeed(filter, currentKeywordsConfig);
    const bannerUrlFeedString = filterFeedString(filter, currentKeywordsConfig);
    const nrmFeedString = nrmFeedToString(nrmFeed);

    const feed = enableNRM ? nrmFeed : bannerUrlFeed;

    const feedString = enableNRM ? nrmFeedString : bannerUrlFeedString;

    window.FINN = window.FINN || ({} as Finn);
    window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
    window.FINN.banner.feed = feed;
    window.FINN.banner.feedString = feedString;
}

function nrmFeedToString(feed: Feed) {
    return feed
        ? Object.entries(feed).reduce((accumulator, currentValue) => {
              accumulator += `${currentValue[0]}=${currentValue[1]};`;
              return accumulator;
          }, '')
        : '';
}

function getSettledPromiseResultOrNull(settledPromise: any) {
    return settledPromise?.status === 'fulfilled' ? settledPromise.value : null;
}

export async function getAsyncKeywords(
    originalFilter: Filter,
    config: HorseshoeConfig,
    isPageLoad: boolean = false,
): Promise<{
    locationKeywords: MappedKeyWords | null;
    searchKeywords: MappedKeyWords | null;
    reklameConfig: ReklameConfiguration | null;
}> {
    const { baseUrl, deviceType } = config;

    // Implement Glimr and consent
    if (baseUrl && getFeatureStatus(UNLEASH_FEATURE_NAMES.enableGlimr)) {
        await loadGlimrIfPermission({ baseUrl });
    }

    const enableNRM = getFeatureStatus('enableNRM', config);

    if (isEmpty(originalFilter)) {
        return {
            locationKeywords: null,
            searchKeywords: null,
            reklameConfig: null,
        };
    } else {
        if (enableNRM) {
            return {
                locationKeywords: null,
                searchKeywords: null,
                reklameConfig: isPageLoad
                    ? null
                    : await getSearchReklameConfig(
                          deviceType,
                          originalFilter,
                          baseUrl,
                      ),
            };
        } else {
            const [locationKeywords, searchKeywords] = await Promise.allSettled(
                [
                    getGeoFilterLocationKeywords(originalFilter, baseUrl),
                    classifiedKeywords(
                        originalFilter as Filter,
                        config.vertical,
                        config.baseUrl,
                    ),
                ],
            );
            return {
                locationKeywords:
                    getSettledPromiseResultOrNull(locationKeywords),
                searchKeywords: getSettledPromiseResultOrNull(searchKeywords),
                reklameConfig: null,
            };
        }
    }
}

function configUpdated(updatedConfig: Filter, config: HorseshoeConfig) {
    if (baseKeywords) {
        // Some travel subverticals don't know who they are before the client is loaded
        if (config.vertical && updatedConfig.subvertical) {
            baseKeywords[
                'no-sno-finn-subsection'
            ] = `${config.vertical}_${updatedConfig.subvertical}`;
            currentSubvertical = updatedConfig.subvertical as Subvertical;
        }
        if (updatedConfig.pageType) {
            baseKeywords['aa-sch-page_type'] = updatedConfig.pageType as string;
        }
    }
}

function refreshBanners() {
    if (clientReady) {
        cycle++;
        markBannerRefresh();
        publish('banner', 'refresh', cycle);
    } else {
        clientReady = true;
        copyQueue();
    }
}

async function filterUpdated(originalFilter: Filter, config: HorseshoeConfig) {
    const vertical = keywords['no-sno-finn-section'] as Vertical;
    verticalSpesificUpdate(config, vertical, currentSubvertical);
    const filter = verticalSpesificPreProccessing(
        originalFilter,
        vertical,
        currentSubvertical,
    );

    const pageType = filter.pageType as PageType;

    if (pageType && pageType !== currentPageType) {
        filter['pagetype'] = [pageType];
        currentPageType = pageType;
    }

    const keywordsUpdate = filterKeywords(filter, currentKeywordsConfig);
    const keywordDiff = diff(currentFilterKeywords, keywordsUpdate);
    const alwaysRefresh = shouldRefresh(vertical, keywordDiff);
    const isPageChange = pageChange(currentFilter, filter);
    const refresh = alwaysRefresh || isPageChange;
    const enableNRM = getFeatureStatus('enableNRM');
    let nrmFeed = window.FINN.banner.feed as Feed;
    const { locationKeywords, searchKeywords, reklameConfig } =
        await getAsyncKeywords(originalFilter as Filter, config);

    currentFilter = filter;
    currentFilterKeywords = keywordsUpdate;

    const nrmKeywords = reklameConfig?.keywords as Keywords;

    const bannerUrlKeywords = {
        ...baseKeywords,
        ...keywordsUpdate,
        ...locationKeywords,
        ...searchKeywords,
    };

    if (reklameConfig?.feed) {
        nrmFeed = reklameConfig?.feed;
    }

    keywords = enableNRM ? nrmKeywords : bannerUrlKeywords;
    keywords = {
        ...keywords,
        ...getGlimrKeywords(),
    };
    if (refresh) {
        if (window.apntag && window.apntag.anq) {
            //@ts-ignore
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    markSetPageOpts();
                    window.apntag.setPageOpts({
                        keywords,
                    });
                }
            });
            refreshBanners();
        }
    }

    updateFeed(filter, nrmFeed);
}

async function loadConfig(config: HorseshoeConfig) {
    clientReady = !config.waitForClient;

    // Check if object is not empty
    if (config?.experiments && Object.keys(config?.experiments).length !== 0) {
        writeExperimentData(config.experiments);
    }

    const appNexusData = config.appNexus;
    currentSubvertical = config.subvertical;
    currentFilter = getFilterData(config);
    currentKeywordsConfig = config.keywordsConfig;
    currentFilterKeywords = filterKeywords(
        currentFilter,
        currentKeywordsConfig,
    );
    currentPageType = config.pageType;

    if (appNexusData && appNexusData.pageOpts) {
        baseKeywords = appNexusData.pageOpts.keywords;

        const { locationKeywords, searchKeywords } = await getAsyncKeywords(
            currentFilter as Filter,
            config,
            true,
        );

        const reklameConfig = config.reklameConfiguration;
        const nrmKeywords = reklameConfig?.keywords as Keywords;
        const nrmFeed = reklameConfig?.feed ? reklameConfig?.feed : {};
        const nrmFeedString = nrmFeedToString(nrmFeed);

        const enableNRM = getFeatureStatus(UNLEASH_FEATURE_NAMES.enableNRM);

        if (enableNRM) {
            keywords = nrmKeywords;
        } else {
            keywords = {
                ...baseKeywords,
                ...currentFilterKeywords,
                ...locationKeywords,
                ...searchKeywords,
            };
        }

        appNexusData.pageOpts.keywords = {
            ...keywords,
            ...getGlimrKeywords(),
        };

        if (enableNRM) {
            const { member, noBidIfUnsold, disablePsa, enableSafeFrame } =
                reklameConfig as ReklameConfiguration;

            appNexusData.pageOpts.member = member;
            appNexusData.pageOpts.noBidIfUnsold = noBidIfUnsold;
            appNexusData.pageOpts.disablePsa = disablePsa;
            appNexusData.pageOpts.enableSafeFrame = enableSafeFrame;
        }

        const pageOpts = appNexusData.pageOpts;
        const initialAppNexusData = { ...appNexusData, ...pageOpts };

        if (window.apntag) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    requestBanners(
                        window.apntag,
                        initialAppNexusData,
                        true,
                        true,
                        enableNRM,
                        nrmFeed as Feed,
                        nrmFeedString,
                    );
                    //TODO need to look at if this should be replaced with data that is coming from nrm?
                    //TODO when is this true?
                    if (
                        appNexusData.tags &&
                        Object.keys(appNexusData.tags).length
                    ) {
                        Object.values(appNexusData.tags).forEach(
                            window.apntag.defineTag,
                        );
                        debouncedLoadTags();
                    }
                }
                if (clientReady) {
                    copyQueue();
                }
            });
        }

        subscribe('banner', 'update', (event) => {
            updateKeywords(event.payload as Filter);
        });

        subscribe('filter', 'updated', (event) => {
            markFilterUpdated();
            filterUpdated(event.payload as Filter, config);
        });

        subscribe('page', 'loaded', (event) => {
            markPageLoaded();
            configUpdated(event.payload as Filter, config);
            filterUpdated(event.payload as Filter, config);
        });

        initializeFeed();

        const props = {
            deviceType: config.deviceType,
            vertical: config.vertical,
            pageType: config.pageType,
        };

        if (!config.configOnly) {
            if (config.skyscraperOnly) {
                renderSkyscraper('horseshoe-root', props);
            } else {
                renderHorseshoe('horseshoe-root', props);
            }
        }
        if (config.pageType === 'object') {
            renderContentBoard('contentboard', {
                ...props,
                id: 'contentboard',
            });
        }

        if (config.deviceType === 'mobile' && config.pageType === 'object') {
            renderBoard('board_1', props);
        }

        verticalSpesificInit(config);
        verticalSpecificContentMarketing(config);
    }
}

function init() {
    markHorseshoeInitiated();
    const config = getPodletConfigFromHtml();

    if (config.version === version) {
        diagnostics.groupCollapsed(
            'displayads-podlet-horseshoe loading version: %s ',
            version,
        );
        diagnostics(config);
        diagnostics.groupEnd();
    } else {
        diagnostics(
            `Server version (${config.version}) does not match client version (${version})`,
        );
    }
    loadConfig(config);
}

markHorseshoeLoaded();
document.addEventListener('DOMContentLoaded', init);
